// 261010 Epic 2 / P3c — bộ điều phối poll THUẦN của hồ sơ phong cách (mẫu `locationViewPoller`). Không React / antd ⇒ test bằng jest fake timers; hook React chỉ bọc mỏng bộ này.
// Luật: truyện có job đề xuất đang chạy (`draftJob`) ⇒ gọi lại mỗi 8 giây · request trước chưa về ⇒ bỏ nhịp · hết job ⇒ dừng và báo "xong" đúng MỘT lần · lỗi mạng ⇒ giữ dữ liệu cũ, báo lỗi, DỪNG (Tải lại mới bật lại).
// "Xong" = lần trước còn thấy job (hoặc host đã `track(jobId)` từ response 202), lần này không còn: job lỗi NHANH thì lượt GET đầu đã không thấy job — `track` để vẫn báo.
import type { StyleProfileView } from '../../types/filmStyleProfile';

export const POLL_MS = 8000;

export const shouldPoll = (v: StyleProfileView | null | undefined): boolean => !!v && !!v.draftJob;

export interface StyleProfilePollerDeps {
  fetchView: () => Promise<StyleProfileView>;
  /** Dữ liệu mới của một lượt poll / Tải lại (không gọi khi lỗi). */
  onData: (v: StyleProfileView) => void;
  /** Job đề xuất vừa kết thúc (thành công hay lỗi — host đọc `failures` để biết). Kèm dữ liệu của CHÍNH lượt đó. */
  onFinished?: (jobId: string, v: StyleProfileView) => void;
  /** Lượt poll / Tải lại lỗi mạng: poll đã dừng, dữ liệu cũ giữ nguyên. */
  onError?: (e: unknown) => void;
  intervalMs?: number;
}

export interface StyleProfilePoller {
  /** Host báo "đây là dữ liệu mới nhất" (nạp đầu, sau thao tác). Bật / tắt timer theo `shouldPoll`; không tự bật lại khi đang lỗi. */
  sync: (v: StyleProfileView) => void;
  /** Host vừa nhận 202 `{ jobId }`: nhớ job này để báo xong kể cả khi lượt GET đầu đã không còn thấy nó. */
  track: (jobId: string) => void;
  /** Nút "Tải lại": gỡ cờ lỗi, nạp ngay một lượt, bật lại poll nếu còn job. */
  reload: () => Promise<void>;
  stop: () => void;
  isActive: () => boolean;
  isErrored: () => boolean;
}

export function createStyleProfilePoller(deps: StyleProfilePollerDeps): StyleProfilePoller {
  const intervalMs = deps.intervalMs ?? POLL_MS;
  let current: StyleProfileView | null = null;
  let tracked: string | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let inflight = false;
  let errored = false;
  let stopped = false;
  let gen = 0; // tăng mỗi lần `sync`: kết quả poll xuất phát TRƯỚC một `sync` mới hơn là dữ liệu cũ ⇒ bỏ

  const clear = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
  const arm = () => {
    if (stopped) return;
    if (!errored && shouldPoll(current)) { if (timer === null) timer = setInterval(() => { void run(false); }, intervalMs); } else clear();
  };
  /** Ghi nhận dữ liệu mới; trả jobId vừa kết thúc (nếu có). */
  const observe = (v: StyleProfileView): string | null => {
    current = v;
    if (v.draftJob) { tracked = v.draftJob.jobId; return null; }
    const done = tracked; tracked = null;
    return done;
  };

  async function run(manual: boolean): Promise<void> {
    if (stopped || inflight) return; // không chồng: bỏ nhịp
    inflight = true;
    const myGen = gen;
    try {
      const r = await deps.fetchView();
      if (stopped) return;
      if (myGen !== gen) return; // đã có dữ liệu mới hơn (sync) ⇒ bỏ kết quả cũ; timer do `sync` lo
      const done = observe(r);
      deps.onData(r);
      if (done) deps.onFinished?.(done, r);
    } catch (e) {
      if (stopped) return;
      errored = true; clear();
      deps.onError?.(e);
      return;
    } finally {
      inflight = false;
    }
    if (manual) errored = false;
    arm();
  }

  return {
    sync(v) {
      if (stopped) return;
      gen += 1;
      const done = observe(v);
      arm();
      if (done) deps.onFinished?.(done, v);
    },
    track(jobId) { if (!stopped) tracked = jobId; },
    async reload() {
      if (stopped) return;
      errored = false;
      await run(true);
    },
    stop() { stopped = true; clear(); },
    isActive: () => timer !== null,
    isErrored: () => errored,
  };
}
